import { Button } from '@nextui-org/react'
import { Button as AntdButton, Input } from 'antd'
import { ArrowBendDownRight } from 'phosphor-react'
import React, { useContext, useEffect, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { Controller, useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { BsCheck2Square, BsX } from 'react-icons/bs'
import { FiUpload } from 'react-icons/fi'
import { useQuery } from 'react-query'
import { useSelector } from 'react-redux'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import HelmetComponent from '../../Component/Helmet'
import happyNews from '../../assets/happyNews.svg'
import j2bdLogo from '../../assets/images/j2bd.svg'
import holloLogo from '../../assets/images/loghollo.png'
import mailBox from '../../assets/mailbox.svg'
import AuthService from '../../services/AuthService'
import { FeathersContext } from '../../services/feathers'
import { checkFeatureFlag } from '../../utils/featureFlag'
import LayoutOnboardingPages from '../TalentPlatform/components/LayoutOnboardingPages/LayoutOnboardingPages'
import * as s from './SoftApply.styles'

const Auth = new AuthService()

// eslint-disable-next-line react/prop-types
function BasicDropZone(props) {
  const { t } = useTranslation(['login', 'common', 'talent', 'talentArea'], {
    useSuspense: false,
  })

  const { acceptedFiles, getRootProps, getInputProps } = useDropzone({
    accept: ['.pdf', '.doc', '.docx'],
    maxFiles: 1,
    multiple: false,
    maxSize: 1024 * 1024 * 5,
    onDropRejected: (data) => {
      // Todo: trad error with data[0]?.errors.code
      if (data?.length && data[0]?.errors?.length) {
        toast.error(data[0]?.errors[0].message)
      }
    },
  })

  const removeFile = (file) => {
    acceptedFiles.splice(file, 1)
    props.setFile(null)
  }

  const files = acceptedFiles.map((file) => (
    <li key={file.path} style={{ display: 'flex', width: '100%' }}>
      <span style={{ flex: 1 }}>
        {file.path} - {file.size} bytes
      </span>
      <BsX
        size={28}
        style={{ cursor: 'pointer', marginRight: 4 }}
        onClick={() => removeFile(file)}
      />
      <BsCheck2Square size={24} color="#20bf6b" />
    </li>
  ))
  useEffect(() => {
    if (acceptedFiles.length) {
      props.setFile(acceptedFiles[0])
    }
  }, [acceptedFiles, props])

  return (
    <s.SoftApply>
      <section>
        {!files.length && (
          <div {...getRootProps({ className: 'dropzone' })}>
            <input {...getInputProps()} />
            <p>
              <FiUpload size={28} color="var(--primary)" />
              <span>{t('common:dragAndDropFile')}</span>
            </p>
          </div>
        )}
        {!!files.length && (
          <aside>
            <ul>{files}</ul>
          </aside>
        )}
      </section>
    </s.SoftApply>
  )
}

/**
 * check if token exists
 * if exists we fill the form and we apply for him
 *
 */

const SoftApply = () => {
  const params = useParams()
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const history = useHistory()
  const feathers = useContext(FeathersContext)
  const { t } = useTranslation(['login', 'common', 'talent', 'talentArea'], {
    useSuspense: false,
  })

  const [file, setFile] = useState(null)
  const [applicationSent, setApplicationSent] = useState(false)
  const [isSendingApplication, setIsSendingApplication] = useState(false)
  const [showConfetti, setShowConfetti] = useState(false)
  const [alreadyApplied, setAlreadyApplied] = useState(false)

  const [companyId, setCompanyId] = useState()
  const [offerId, setOfferId] = useState()
  const [teamId, setTeamId] = useState()
  const [offerName, setOfferName] = useState()

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm()
  const talent = useSelector((state) => state.talent)

  const { data: company } = useQuery({
    enabled: Boolean(queryParams.get('companyId')),
    queryKey: ['company', queryParams.get('companyId')],
    queryFn: async () =>
      feathers.service('lambda-talent').find({
        query: {
          type: 'getCompany',
          companyId: queryParams.get('companyId'),
        },
      }),
  })

  const handleTalentExistApply = async () => {
    setIsSendingApplication(true)
    const companyId = queryParams.get('companyId')
    const teamId = queryParams.get('teamId')

    try {
      // We can loop first in talent candidateIds and try to find if already exist
      const candidate = await Auth.signup(companyId, {
        firstname: talent.firstname,
        lastname: talent.lastname,
        email: talent.email,
      })

      const conversations = await feathers.service('lambda-conversation').find({
        query: {
          candidate_id: candidate._id,
          offer_id: params.id,
        },
      })

      if (conversations.total) {
        // toast.error('Vous avez déjà candidaté pour cette offre')
        setAlreadyApplied(true)
        return
      }

      const convData = {
        candidate_id: candidate._id,
        offer_id: params.id,
        company_id: companyId,
        team_id: teamId,
        createdBy: 'softApply',
      }

      const file = { ...talent.resume }
      delete file.signedUrl

      convData.files = [file]

      await feathers.service('lambda-conversation').create(convData)

      toast.success('Votre candidature a été envoyée avec succès !', {
        duration: 3200,
      })
      setTimeout(() => {
        history.push('/app/applications', { fromSoftApply: true })
      }, 200)
    } catch (error) {
      console.error(error)
    } finally {
      setIsSendingApplication(false)
    }
  }

  const onSubmit = async ({ firstname, lastname, email }) => {
    if (!file) {
      toast.error(
        t(
          'talentArea:requiredResume',
          'It seems that you forgot to upload your CV, this document is mandatory.',
        ),
      )
      return
    }
    try {
      setIsSendingApplication(true)
      const candidate = await Auth.signup(
        companyId,
        {
          firstname,
          lastname,
          email,
        },
        false,
        checkFeatureFlag,
      )

      // Manage conversations
      const conversation = await Auth.fetch(
        `/lambda-conversation?candidate_id=${candidate._id}&offer_id=${offerId}`,
        {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
        },
      )

      const { total } = conversation

      if (total !== 0) {
        toast.success('Vous avez déjà candidaté pour cette offre')
        setAlreadyApplied(true)
      }

      // Manage file upload
      const formData = new FormData()

      formData.append('uri', file)
      formData.append('name', file?.name)
      formData.append('type', 'resume')
      formData.append('candidateId', candidate._id)

      await Auth.fetch('/lambda-candidate-file', {
        method: 'POST',
        body: formData,
      }).catch((e) => {
        console.error(e)
      })

      await Auth.fetch('/lambda-conversation', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          candidate_id: candidate._id,
          offer_id: offerId,
          company_id: companyId,
          team_id: teamId,
          createdBy: 'softApply',
          domainJ2BD: checkFeatureFlag ? true : undefined,
        }),
      })
      setApplicationSent(true)
      // setShowConfetti(true)
    } catch (e) {
      console.error(e)
    } finally {
      setIsSendingApplication(false)
    }
  }

  // set initial state data
  useEffect(() => {
    setOfferId(params.id)
    setCompanyId(queryParams.get('companyId'))
    setTeamId(queryParams.get('teamId'))
    setOfferName(queryParams.get('offerName'))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    // This is the only way to know if talent clicked on url to complete its profile
    // And we check if it has uploaded a resume otherwise we cannot apply automatically
    if (talent?.isVerified && Object.keys(talent.resume).length) {
      handleTalentExistApply()
    }
  }, [talent])

  if (applicationSent || alreadyApplied) {
    return (
      <s.SoftApplyComfirm>
        <HelmetComponent
          faviconUrl={checkFeatureFlag ? j2bdLogo : holloLogo}
          name={checkFeatureFlag ? 'J2BD' : 'Hollo'}
        />
        {alreadyApplied ? (
          <>
            <img src={happyNews} height={180} alt="application-sent" />
            <p className="title-already-applied">
              Vous avez <strong>déjà candidaté</strong> pour cette offre !{' '}
              <br />
              Vous pouvez retrouver vos candidatures dans votre{' '}
              <strong>espace candidat</strong>.
            </p>
          </>
        ) : (
          <>
            <img src={mailBox} height={180} alt="application-sent" />
            <p className="title">
              Pour finaliser votre candidature, <br />
              <div>
                <strong> rendez-vous sur votre boîte mail ! </strong>
              </div>
              <ArrowBendDownRight size={32} />
            </p>
          </>
        )}
        {alreadyApplied ? (
          <Button
            className="basic-btn"
            onClick={() => history.push('/app/applications')}
          >
            J&apos;accède à mon espace candidat
          </Button>
        ) : undefined}
      </s.SoftApplyComfirm>
    )
  }
  return (
    <div>
      <HelmetComponent
        faviconUrl={checkFeatureFlag ? j2bdLogo : holloLogo}
        name={checkFeatureFlag ? 'J2BD Partners' : 'Hollo'}
      />
      <header>
        <img
          src={checkFeatureFlag ? j2bdLogo : holloLogo}
          height={40}
          alt=""
          className="logo"
        />
        <h1
          style={{
            fontSize: '24px',
            fontWeight: '600',
            lineHeight: '40px',
          }}
        >
          {t('talent:applyTo')}{' '}
        </h1>
      </header>
      <form
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 15,
        }}
        onSubmit={handleSubmit(onSubmit)}
      >
        <div style={{ display: 'flex', gap: 10 }}>
          <Controller
            name="firstname"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <Input {...field} placeholder={t('common:firstname')} />
            )}
          />
          {errors.firstname && (
            <span className="error-field">
              {t('login:inputs.fieldRequired.error')}
            </span>
          )}
          <Controller
            name="lastname"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <Input {...field} required placeholder={t('common:lastname')} />
            )}
          />
          {errors.lastname && (
            <span className="error-field">
              {t('login:inputs.fieldRequired.error')}
            </span>
          )}
        </div>

        <Controller
          name="email"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <Input {...field} placeholder={t('common:email')} />
          )}
        />
        {errors.email && (
          <span className="error-field">
            {t('login:inputs.fieldRequired.error')}
          </span>
        )}
        <BasicDropZone setFile={setFile} />
        <footer>
          <p>
            En continuant vous acceptez les politiques de confidentialité de{' '}
            <a
              href={
                checkFeatureFlag
                  ? 'https://www.job2bedone-partners.com/mentions-legales'
                  : 'https://hollo.io/privacy-policy'
              }
              target="_blank"
              rel="noreferrer"
            >
              {checkFeatureFlag ? 'J2BD' : 'Hollo'}
            </a>
            {!checkFeatureFlag ? (
              <>
                {' '}
                ainsi que les{' '}
                <a
                  href={company?.policyUrl || 'https://company.com/policy'}
                  target="_blank"
                  rel="noreferrer"
                >
                  notres
                </a>
              </>
            ) : undefined}
            .
          </p>
          <AntdButton
            style={{ fontSize: '18px', width: '100%' }}
            className="basic-btn"
            type="submit"
            htmlType="submit"
            loading={isSendingApplication}
          >
            {t('talentArea:submit', 'Je postule')}
          </AntdButton>

          <AntdButton
            style={{ fontSize: '14px', width: '100%', marginTop: '20px' }}
            type="link"
            onClick={() => history.push('/login')}
          >
            Vous avez déjà un compte ? Connectez-vous →
          </AntdButton>
        </footer>
      </form>
    </div>
  )
}

const Wrapper = () => (
  <LayoutOnboardingPages type="soft-apply">
    <SoftApply />
  </LayoutOnboardingPages>
)

export default Wrapper
